// ABOUTME: Admin API client for REST-based file uploads (multipart/form-data).
// ABOUTME: tRPC cannot handle file uploads, so this stays as a direct fetch call.

import { supabase } from '@/lib/supabase';
import { baseUrl } from '@/lib/trpc';

/**
 * Processing result from file upload
 */
export interface ProcessingResult {
    success: boolean;
    formatId: string;
    targetTable: string;
    totalRows: number;
    insertedRows: number;
    failedRows: number;
    errors: Array<{
        rowNumber: number;
        error: string;
    }>;
    processingTimeMs: number;
}

/**
 * Upload a file for processing
 */
export async function uploadFile(
    file: File,
    formatId: string,
    workspaceId?: string
): Promise<ProcessingResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('formatId', formatId);
    if (workspaceId) {
        formData.append('workspaceId', workspaceId);
    }

    const url = `${baseUrl}/api/admin/upload`;

    // Get the access token for authentication
    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token;

    const response = await fetch(url, {
        method: 'POST',
        body: formData,
        headers: {
            // Note: Don't set Content-Type header - browser will set it with boundary for multipart
            ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
        },
    });

    const json = await response.json();

    if (!response.ok) {
        // Return the processing result if available in the error response
        if (json.data) {
            return json.data as ProcessingResult;
        }
        throw new Error(json.error?.message || 'Upload failed');
    }

    if (!json.success) {
        throw new Error(json.message || 'Upload failed');
    }

    return json.data as ProcessingResult;
}
